import React, { useEffect, useRef } from 'react';
import r34 from '../assets/images/r34.jpg';

import '../fadeScaleAnim.css'

const About = () => {
  const parallaxRef = useRef(null);
  const misionRef = useRef(null);
  const visionRef = useRef(null);
  const valoresRef = useRef(null);
  const politicaRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const img = parallaxRef.current;
      if (!img) return;
      
      const rect = img.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Solo aplica el efecto si la imagen está en pantalla
      if (rect.top < windowHeight && rect.bottom > 0) {
        // Calcula el porcentaje de scroll relativo a la posición del elemento
        const elementCenter = rect.top + rect.height / 2;
        const windowCenter = windowHeight / 2;
        const distanceFromCenter = elementCenter - windowCenter;
        
        // Efecto parallax más suave y controlado
        const parallaxSpeed = 0.3;
        const offsetY = distanceFromCenter * parallaxSpeed;
        const scale = 1 + (Math.abs(distanceFromCenter) / windowHeight) * 0.05;
        
        // Aplica la transformación con GPU acceleration
        img.style.transform = `translate3d(0, ${offsetY}px, 0) scale(${Math.min(scale, 1.1)})`;
      }
    };    const handleTextReveal = () => {
      const elements = [
        { ref: misionRef, delay: 200 },
        { ref: visionRef, delay: 400 },
        { ref: valoresRef, delay: 600 },
        { ref: politicaRef, delay: 800 }
      ];

      elements.forEach(({ ref, delay }) => {
        const element = ref.current;
        if (!element) return;

        const rect = element.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        // Activa la animación cuando el elemento entra en pantalla (75% visible)
        if (rect.top < windowHeight * 0.75 && rect.bottom > 0) {
          setTimeout(() => {
            element.classList.add('reveal-text');
          }, delay);
        }
      });
    };
    
    // Throttle scroll events para mejor rendimiento
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          handleTextReveal();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', throttledScroll, { passive: true });
    // Ejecutar una vez al cargar para elementos ya visibles
    throttledScroll();
    
    return () => window.removeEventListener('scroll', throttledScroll);
  }, []);

  return (
    <section id="nosotros" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 tracking-wide">
            ACERCA DE NOSOTROS
          </h2>
        </div>

        {/* Layout principal con imagen a la izquierda y contenido a la derecha */}
        <div className="flex flex-col lg:flex-row gap-0 min-h-[600px]">
            {/* Imagen de granos - lado izquierdo */}
          <div className="w-full lg:w-1/2 relative parallax-container flex items-center justify-center">
            <img 
              ref={parallaxRef}
              src={r34} 
              alt="Granos y legumbres variados" 
              className="w-full h-full object-cover rounded-3xl shadow-lg fade-scale-anim paradise-img parallax-img"
              style={{animationDelay: '0.1s'}}
            />
          </div>

          {/* Contenido - lado derecho */}
          <div className="w-full lg:w-1/2 bg-gray-50 p-8 lg:p-12 flex flex-col justify-center">
              {/* Misión */}
            <div className="mb-10 text-reveal" ref={misionRef}>
              <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Misión
              </h3>
              <p className="text-gray-600 text-base lg:text-lg leading-relaxed">
                En Industrias Campo Fresco ofrecemos <strong className="text-gray-800">productos alimenticios de alta calidad</strong>, 
                que nos distinguen como una organización comprometida con la <strong className="text-gray-800">satisfacción total del cliente</strong>, 
                respaldados por el <strong className="text-gray-800">respeto a nuestros socios comerciales, colaboradores y con el medio ambiente</strong>, 
                ofreciendo así <strong className="text-green-700">Alimentos con Valor</strong>.
              </p>
            </div>            {/* Visión */}
            <div className="mb-10 text-reveal" ref={visionRef}>
              <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Visión
              </h3>
              <p className="text-gray-600 text-base lg:text-lg leading-relaxed">
                Consolidarnos como una <strong className="text-gray-800">empresa líder en el giro de alimentos, granos, especias y semillas </strong> 
                en el <strong className="text-gray-800">mercado nacional</strong>, <strong className="text-gray-800">posicionar nuestras marcas</strong>, siendo la mejor opción como <strong className="text-gray-800">socios comerciales </strong> 
                de empresas que pretenden llevar al consumidor final <strong className="text-green-700">alimentos con valor</strong>, e impulsando el <strong className="text-gray-800">crecimiento 
                sostenido de la organización</strong> y todos nuestros colaboradores, utilizando <strong className="text-gray-800">tecnología de punta</strong> y 
                <strong className="text-gray-800"> prácticas basadas en valores</strong>.
              </p>
            </div>

            {/* Valores */}
            <div className="text-reveal" ref={valoresRef}>
              <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Valores
              </h3>
              <div className="grid grid-cols-2 gap-3 text-gray-700">                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span className="text-base lg:text-lg"><strong className="text-gray-800">Colaboración</strong></span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span className="text-base lg:text-lg"><strong className="text-gray-800">Honestidad</strong></span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span className="text-base lg:text-lg"><strong className="text-gray-800">Confianza</strong></span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span className="text-base lg:text-lg"><strong className="text-gray-800">Responsabilidad</strong></span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span className="text-base lg:text-lg"><strong className="text-gray-800">Lealtad</strong></span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span className="text-base lg:text-lg"><strong className="text-gray-800">Ética</strong></span>
                </div>
              </div>
                      {/* Sección adicional: Política de Inocuidad */}
        <div className="mt-20 bg-gray-50 rounded-lg text-reveal " ref={politicaRef}>
          <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 text-left">
            Política de Inocuidad
          </h3>          <p className="text-gray-600 text-base lg:text-lg leading-relaxed text-left max-w-5xl mx-auto">
            En Industrias Campo Fresco S.A. de C.V. estamos comprometidos en <strong className="text-gray-800">satisfacer las necesidades 
            y expectativas de nuestros clientes y partes interesadas</strong>, mediante el <strong className="text-gray-800">cumplimiento de requisitos 
            legales, reglamentarios y de nuestros clientes</strong>, tomando el contexto de la organización aseguramos 
            <strong className="text-gray-800">estrictos estándares de calidad e inocuidad</strong> para los productos elaborados, la <strong className="text-gray-800">salud y competencia 
            de nuestros colaboradores</strong>, con <strong className="text-gray-800">objetivos de calidad e inocuidad definidos</strong>, que nos permitan el 
            cumplimiento de nuestra visión y misión, manteniendo una <strong className="text-gray-800">comunicación interna y externa</strong> que permita 
            dar respuesta a las demandas de información relativas al <strong className="text-green-700">sistema de gestión de inocuidad de los alimentos</strong>, 
            basado en un esquema de <strong className="text-green-700">mejora continua</strong> en todos nuestros productos, procesos y servicios, 
            dentro de una <strong className="text-green-700">cultura de inocuidad</strong>.
          </p>
        </div>
            </div>
          </div>
        </div>

        {/* Sección de Certificaciones (descomentada y mejorada) */}
        {/* <div className="mt-20 text-center">
          <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12">Nuestras Certificaciones</h3>
          <div className="flex flex-wrap justify-center items-center gap-8 lg:gap-16">
            <div className="bg-white p-6 lg:p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
              <img 
                src={fdaLogo} 
                alt="Certificación FDA" 
                className="h-16 lg:h-20 object-contain mx-auto" 
              />
            </div>
            <div className="bg-white p-6 lg:p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
              <img 
                src={primusLogo} 
                alt="Certificación Primus GFS" 
                className="h-16 lg:h-20 object-contain mx-auto" 
              />
            </div>
            <div className="bg-white p-6 lg:p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
              <img 
                src={fsscLogo} 
                alt="Certificación FSSC 22000" 
                className="h-16 lg:h-20 object-contain mx-auto" 
              />
            </div>
          </div>
        </div> */}
        
      </div>
    </section>
  );
};

export default About;